import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../lib/api'
import Layout from '../components/Layout'
import { Card, Spinner, ErrorMessage, Button, Notice } from '../components/ui'
import { points, adp, formatLabel } from '../lib/format'
import SelectionChips from '../components/SelectionChips'
import {
  previousParticipant,
  destinationsFor,
  availableTeams,
} from '../lib/participants'

// Tailwind scans for whole class names, so these cannot be built from the
// participant count at runtime.
const PARTICIPANT_GRID = {
  1: '',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
}

// A sandbox, not a proposal. The builder makes deals that could really happen —
// each side gives up players it actually rosters. Here any player in the league
// can be dropped onto either side, so the question becomes "what would my team
// look like with these players on it" rather than "would they say yes".
//
// The teams a player is really on are left untouched: take someone off a third
// team and that team keeps them. The trade is a clone, not a transfer, and the
// result page says so.

function PlayerRow({ player, destinations, ownerName, destination, onAssign, onClear, disabledFor }) {
  return (
    <li>
      <div
        className={`flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-sm ${
          destination
            ? 'border-emerald-500 bg-emerald-950/40'
            : 'border-transparent hover:border-slate-600'
        }`}
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-slate-100">{player.name}</span>
          <span className="block truncate text-xs text-slate-400">
            {player.position}
            {player.team ? ` · ${player.team}` : ''} · ADP {adp(player.adp)}
            {ownerName ? ` · ${ownerName}` : ''}
          </span>
        </span>

        <span className="shrink-0 text-right text-xs tabular-nums text-slate-300">
          {points(player.projected_points)}
          <span className="block text-[10px] uppercase text-slate-400">proj</span>
        </span>

        <span className="flex shrink-0 items-center gap-1">
          {destinations.map(({ id, label }) => {
            const active = destination === id
            // A team cannot receive someone it already has: the roster would
            // hold two of the same player and the optimizer would happily
            // start them twice.
            const owned = disabledFor === id

            return (
              <button
                key={id}
                type="button"
                aria-pressed={active}
                disabled={owned}
                title={owned ? `Already on ${label}` : `Send to ${label}`}
                onClick={() => (active ? onClear() : onAssign(id))}
                className={`rounded-md border px-2 py-1 text-xs font-semibold transition ${
                  active
                    ? 'border-emerald-600 bg-emerald-600 text-white'
                    : owned
                      ? 'cursor-not-allowed border-slate-800 text-slate-700'
                      : 'border-slate-600 text-slate-200 hover:border-slate-400'
                }`}
              >
                {owned ? '—' : `→ ${label}`}
              </button>
            )
          })}
        </span>
      </div>
    </li>
  )
}

export default function TradeExplorer() {
  const { leagueId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const league = useQuery({
    queryKey: ['league', leagueId],
    queryFn: () => api.league(leagueId),
  })

  // No teamId: the whole league is the pool, which is the point of this page.
  const players = useQuery({
    queryKey: ['leaguePlayers', leagueId, undefined],
    queryFn: () => api.leaguePlayers(leagueId),
  })

  const [participantIds, setParticipantIds] = useState([])
  // player id -> { player, fromTeamId, toTeamId }. Keyed by player so nobody
  // can land on two sides at once, which mirrors the one-row-per-player unique
  // index.
  const [selection, setSelection] = useState(new Map())
  const [search, setSearch] = useState('')

  const teams = league.data?.teams || []

  useEffect(() => {
    if (!league.data || participantIds.length > 0) return

    const mine = league.data.league.my_team_id || teams[0]?.id
    const other = teams.find((t) => t.id !== mine)?.id
    setParticipantIds([mine, other].filter(Boolean))
  }, [league.data, participantIds, teams])

  const sync = useMutation({
    mutationFn: () => api.syncLeague(leagueId),
    onSuccess: () => queryClient.invalidateQueries(),
  })

  const create = useMutation({
    mutationFn: () => {
      // from_team_id is another side of the deal, never the real owner. Naming
      // the real owner would pull them into the trade and strip the player off
      // their roster; naming a side that is already here leaves them alone,
      // which is the semantics this page promises.
      //
      // With two participants that has one answer and the picker never appears.
      // With three or more it does not, so the source is chosen rather than
      // inferred — it decides which side is told it gave up someone it never
      // rostered, and guessing that would be putting words in the user's mouth.
      const assets = [...selection.values()].map(({ player, fromTeamId, toTeamId }) => ({
        player_id: player.id,
        from_team_id: fromTeamId,
        to_team_id: toTeamId,
      }))
      return api.createTrade(leagueId, assets, { exploratory: true })
    },
    onSuccess: (data) => navigate(`/trades/${data.trade.id}`),
  })

  const all = players.data?.players || []

  const visible = useMemo(() => {
    if (!search.trim()) return all
    const needle = search.trim().toLowerCase()
    return all.filter(
      (p) =>
        p.name.toLowerCase().includes(needle) ||
        (p.position || '').toLowerCase() === needle ||
        (p.team || '').toLowerCase() === needle
    )
  }, [all, search])

  if (league.isPending || players.isPending) {
    return (
      <Layout wide>
        <Spinner label="Loading league…" />
      </Layout>
    )
  }

  if (league.error || players.error) {
    return (
      <Layout wide>
        <ErrorMessage error={league.error || players.error} />
      </Layout>
    )
  }

  const info = league.data.league
  const health = info.data_health
  const maxTeams = info.max_trade_teams || 2
  const multiTeam = Boolean(info.supports_multi_team_trades)
  const byId = new Map(teams.map((t) => [t.id, t]))
  const participants = participantIds.map((id) => byId.get(id)).filter(Boolean)
  const nameFor = (id) => byId.get(id)?.name

  const assign = (player, toTeamId) => {
    setSelection((current) => {
      const existing = current.get(player.id)
      // Keep a source the user already chose; otherwise fall back to the team
      // before the destination in the ring, which for two participants is
      // simply the other one.
      const fromTeamId =
        existing && existing.fromTeamId !== toTeamId
          ? existing.fromTeamId
          : previousParticipant(participantIds, toTeamId)

      return new Map(current).set(player.id, { player, fromTeamId, toTeamId })
    })
  }

  const source = (player, fromTeamId) => {
    setSelection((current) => {
      const entry = current.get(player.id)
      if (!entry) return current

      return new Map(current).set(player.id, { ...entry, fromTeamId })
    })
  }

  const addParticipant = () => {
    const candidate = availableTeams(teams, participantIds)[0]
    if (candidate) setParticipantIds([...participantIds, candidate.id])
  }

  const dropParticipant = (teamId) => {
    const remaining = participantIds.filter((id) => id !== teamId)
    setParticipantIds(remaining)
    setSelection((current) => {
      const next = new Map()
      current.forEach((entry, playerId) => {
        if (entry.toTeamId === teamId) return

        const fromTeamId =
          entry.fromTeamId === teamId
            ? previousParticipant(remaining, entry.toTeamId)
            : entry.fromTeamId

        if (fromTeamId) next.set(playerId, { ...entry, fromTeamId })
      })
      return next
    })
  }

  const changeParticipant = (index, teamId) => {
    const previous = participantIds[index]
    if (previous === teamId) return

    const updated = participantIds.map((id, i) => (i === index ? teamId : id))
    setParticipantIds(updated)
    setSelection((current) => {
      const next = new Map()
      current.forEach((entry, playerId) => {
        if (entry.toTeamId === previous) return

        const fromTeamId =
          entry.fromTeamId === previous
            ? previousParticipant(updated, entry.toTeamId)
            : entry.fromTeamId

        if (fromTeamId) next.set(playerId, { ...entry, fromTeamId })
      })
      return next
    })
  }

  const clear = (playerId) => {
    setSelection((current) => {
      const next = new Map(current)
      next.delete(playerId)
      return next
    })
  }

  const remove = (player) => clear(player.id)

  const chosen = [...selection.values()]
  const ready = chosen.length > 0

  const targets = participants.map((t) => ({ id: t.id, label: t.name }))

  return (
    <Layout wide>
      <div className="mb-4">
        <h1 className="text-lg font-semibold text-slate-100">
          Explore a trade
        </h1>
        <p className="text-sm text-slate-400">
          {league.data.league.name} · Week {league.data.current_week} ·{' '}
          {formatLabel(league.data.league.format?.ppr_type)}
          {league.data.league.format?.superflex ? ' · Superflex' : ''}
        </p>
        <p className="mt-1 text-sm">
          <Link
            to={`/leagues/${leagueId}/trade`}
            className="underline underline-offset-2 text-slate-300 hover:text-slate-100"
          >
            ← Build a real trade instead
          </Link>
        </p>
      </div>

      <div className="mb-4">
        <Notice title="Anyone in the league, on either side">
          Ownership is ignored here. A player you take from a third team stays on
          that team as well, so this answers “what would this roster do” — not
          “would they accept”.
        </Notice>
      </div>

      {health && !health.projections_present && (
        <div className="mb-4">
          <Notice title={`No projections synced for week ${health.projected_week}`}>
            Points and win odds will all read zero until projections are pulled.
            Run <code className="font-mono">bin/rails sleeper:bootstrap</code>.
          </Notice>
        </div>
      )}

      <div className="mb-4">
        <div className={`grid gap-3 ${PARTICIPANT_GRID[participants.length] || ''}`}>
          {participantIds.map((id, index) => (
            <label key={id} className="block">
              <span className="mb-1 flex items-center justify-between text-xs font-medium text-slate-300">
                <span>Team {String.fromCharCode(65 + index)}</span>
                {/* Never below two: a trade has to have two sides. */}
                {participantIds.length > 2 && (
                  <button
                    type="button"
                    onClick={() => dropParticipant(id)}
                    className="text-slate-400 hover:text-rose-300"
                  >
                    Remove
                  </button>
                )}
              </span>
              <select
                className="w-full rounded-md px-3 py-2 text-sm border border-slate-600 bg-slate-900"
                value={id}
                onChange={(e) => changeParticipant(index, e.target.value)}
              >
                {teams
                  .filter((team) => team.id === id || !participantIds.includes(team.id))
                  .map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
              </select>
            </label>
          ))}
        </div>

        {multiTeam && (
          <div className="mt-2 flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={addParticipant}
              disabled={
                participantIds.length >= maxTeams || participantIds.length >= teams.length
              }
            >
              Add a team
            </Button>
            <span className="text-xs text-slate-400">
              {participantIds.length >= maxTeams
                ? `A trade can involve at most ${maxTeams} teams.`
                : `Up to ${maxTeams} teams can be involved.`}
            </span>
          </div>
        )}
      </div>

      <Card title="Every rostered player in the league">
        <div className="mb-3 flex items-center gap-2">
          <input
            className="min-w-0 flex-1 rounded-md px-3 py-1.5 text-sm border border-slate-600 bg-slate-900"
            placeholder="Search name, position, or NFL team"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="shrink-0 text-xs tabular-nums text-slate-400">
            {search.trim() ? `${visible.length}/${all.length}` : `${all.length}`}
          </span>
        </div>

        {all.length === 0 && (
          <Notice
            title="No players on any roster"
            action={
              <Button variant="secondary" onClick={() => sync.mutate()} disabled={sync.isPending}>
                {sync.isPending ? 'Syncing…' : 'Sync league'}
              </Button>
            }
          >
            The league synced before its players did. Syncing again will fill in
            the rosters.
          </Notice>
        )}

        {all.length > 0 && visible.length === 0 && (
          <p className="text-sm text-slate-400">
            No players match “{search.trim()}”.
          </p>
        )}

        {visible.length > 0 && (
          <ul className="max-h-[32rem] space-y-1 overflow-y-auto">
            {visible.map((player) => (
              <PlayerRow
                key={player.id}
                player={player}
                destinations={targets}
                ownerName={nameFor(player.owner_team_id)}
                destination={selection.get(player.id)?.toTeamId ?? null}
                disabledFor={player.owner_team_id}
                onAssign={(toTeamId) => assign(player, toTeamId)}
                onClear={() => clear(player.id)}
              />
            ))}
          </ul>
        )}
      </Card>

      <div className="sticky bottom-0 mt-4 border-t py-3 backdrop-blur border-slate-800 bg-slate-950/95">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-6">
          <div className="flex min-w-0 flex-1 flex-wrap gap-6">
            {participants.map((team) => (
              <SelectionChips
                key={team.id}
                label={`${team.name} receives`}
                entries={chosen.filter((c) => c.toTeamId === team.id)}
                onRemove={remove}
                onRoute={source}
                targets={destinationsFor(participantIds, team.id).map((id) => ({
                  id,
                  label: nameFor(id),
                }))}
                routeValue={(entry) => entry.fromTeamId}
                routeArrow="←"
                routeHint="comes from"
              />
            ))}
          </div>

          <div className="flex items-center gap-2 sm:shrink-0 sm:self-center">
            {chosen.length > 0 && (
              <Button variant="secondary" onClick={() => setSelection(new Map())}>
                Clear
              </Button>
            )}
            <Button
              className="flex-1 sm:flex-none"
              disabled={!ready || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Evaluating…' : 'Evaluate'}
            </Button>
          </div>
        </div>

        {!ready && (
          <p className="mt-2 text-xs text-slate-400">
            Send at least one player to a side. One side alone is a fine
            question to ask here.
          </p>
        )}

        <div className="mt-2">
          <ErrorMessage error={create.error} />
          <ErrorMessage error={sync.error} />
        </div>
      </div>
    </Layout>
  )
}
